import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import { teamRolesApiRef } from '../plugin';
import { DeleteTeamRoleParams } from '@agilelab/plugin-wb-team-roles-common';
import { TeamRoleAssignmentKindEnum } from '../types';
import { QUERY_KEY_TEAM_ROLE_ASSIGNMENTS } from '../constants';

type DeleteTeamRoleAssignmentParams = DeleteTeamRoleParams & {
  assignmentType: TeamRoleAssignmentKindEnum;
};

export const useDeleteTeamRoleAssignment = (options?: {
  alertOnError: boolean;
}) => {
  const identityApi = useApi(identityApiRef);
  const teamRolesApi = useApi(teamRolesApiRef);
  const alertApi = useApi(customAlertApiRef);

  const queryClient = useQueryClient();

  const defaultOnSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_TEAM_ROLE_ASSIGNMENTS],
    });
    alertApi.post({
      severity: 'success',
      message: 'Team role assignment deleted successfully',
    });
  };

  return useMutation({
    mutationFn: async ({
      assignmentType,
      ...params
    }: DeleteTeamRoleAssignmentParams) => {
      const credentials = await identityApi.getCredentials();
      const payload = { ...params, options: credentials };

      if (assignmentType === TeamRoleAssignmentKindEnum.FULL) {
        return teamRolesApi.deleteAssignee(payload);
      }

      return teamRolesApi.deleteLimitedAssignee(payload);
    },
    onSuccess: defaultOnSuccess,
    onError: e => {
      if (options?.alertOnError === false) return;
      alertApi.post({
        severity: 'error',
        message: (e instanceof Error ? e : new Error('Something went wrong'))
          .message,
      });
    },
  });
};
